import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const defaultInvoice = () => {
  return {
    _id: '_new',
    number: '_new',
    invoiceType: 'RECEIVABLE',
    invoiceState: 'DRAFT',
    invoiceNumber: '',
    grossAmount: null,
    netAmount: null,
    taxAmount: null,
    vatPercentage: 19,
    invoiceDate: null,
    dueDate: null,
    paidDate: null,
    project: null,
    supplier: null,
    notes: '',
    attachments: [],
    createdBy: null,
  };
};

const getDefaultState = () => {
  return {
    invoices: [],
    invoiceCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an invoice list view
 * used for paginated invoice queries
 * @type {DocumentNode}
 */
const INVOICE_LIST_REQUEST_FIELDS = gql`
  fragment invoiceListFields on Invoice {
    _id
    number
    invoiceType
    invoiceState
    invoiceNumber
    grossAmount
    netAmount
    invoiceDate
    dueDate
    paidDate
    supplier {
      _id
      number
      name
    }
    project {
      id
      number
      customer {
        firstname
        lastname
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * Defines the fields which are requested for an invoice
 * used for all queries/mutations which return an invoice
 * @type {DocumentNode}
 */
const INVOICE_REQUEST_FIELDS = gql`
  fragment invoiceFields on Invoice {
    _id
    number
    invoiceType
    invoiceState
    invoiceNumber
    grossAmount
    netAmount
    taxAmount
    vatPercentage
    invoiceDate
    dueDate
    paidDate
    notes
    attachments {
      id
      path
      filename
      displayFilename
      size
      mimetype
      attachmentType
    }
    attachmentTypes
    supplier {
      _id
      number
      name
    }
    project {
      id
      number
      customer {
        firstname
        lastname
        street
        streetNumber
        streetNumberSuffix
        city
        zip
      }
    }
    createdBy {
      _id
      firstname
      lastname
      email
    }
    updatedBy {
      _id
      firstname
      lastname
      email
    }
    createdAt
    updatedAt
  }
`;

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} invoice
 */
const createPayload = (invoice) => {
  const payload = {
    invoiceType: invoice.invoiceType,
    invoiceState: invoice.invoiceState,
    invoiceNumber: invoice.invoiceNumber,
    grossAmount: invoice.grossAmount,
    invoiceDate: invoice.invoiceDate,
  };

  // Add optional fields only if they have values
  if (invoice.netAmount !== null && invoice.netAmount !== undefined) {
    payload.netAmount = invoice.netAmount;
  }
  if (invoice.taxAmount !== null && invoice.taxAmount !== undefined) {
    payload.taxAmount = invoice.taxAmount;
  }
  if (invoice.vatPercentage !== undefined) {
    payload.vatPercentage = invoice.vatPercentage;
  }
  if (invoice.dueDate) {
    payload.dueDate = invoice.dueDate;
  }
  if (invoice.paidDate) {
    payload.paidDate = invoice.paidDate;
  }
  if (invoice.project) {
    payload.project = invoice.project.id || invoice.project._id || invoice.project;
  }
  if (invoice.supplier) {
    payload.supplier = invoice.supplier._id || invoice.supplier;
  }
  if (invoice.notes) {
    payload.notes = invoice.notes;
  }

  return payload;
};

/**
 * Payload of the block project-invoices (`ProjectInvoiceInput`): without project, the server sets
 * it.
 * @param {*} invoice
 */
const createProjectInvoicePayload = (invoice) => {
  const payload = createPayload(invoice);
  delete payload.project;
  return payload;
};

const INVOICE_LIST_QUERY = gql`
  query invoicesPaginated(
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: InvoiceFilterInput
  ) {
    invoicesPaginated(pagination: $pagination, sorting: $sorting, filters: $filters) {
      invoices {
        ...invoiceListFields
      }
      invoiceCount
    }
  }
  ${INVOICE_LIST_REQUEST_FIELDS}
`;

/** Invoice list of the block project-invoices; the server restricts it to the project. */
const PROJECT_INVOICE_LIST_QUERY = gql`
  query projectInvoices(
    $projectNumber: Int!
    $pagination: PaginationInput
    $sorting: SortingInput
    $filters: InvoiceFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        invoices {
          id
          number
          invoices(pagination: $pagination, sorting: $sorting, filters: $filters) {
            invoices {
              ...invoiceListFields
            }
            invoiceCount
          }
        }
      }
    }
  }
  ${INVOICE_LIST_REQUEST_FIELDS}
`;

/**
 * Request of the invoice list: with a project number the block project-invoices, without it the
 * global query invoicesPaginated. `pagesOf` returns the `InvoicePages` of the response.
 */
const invoiceListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_INVOICE_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.invoices.invoices,
    };
  }
  return { query: INVOICE_LIST_QUERY, pagesOf: (data) => data.invoicesPaginated };
};

const INVOICE_BY_NUMBER_QUERY = gql`
  query invoiceByNumber($invoiceNumber: Int!) {
    invoiceByNumber(invoiceNumber: $invoiceNumber) {
      ...invoiceFields
    }
  }
  ${INVOICE_REQUEST_FIELDS}
`;

/** One invoice of the block project-invoices; an invoice of another project is not found. */
const PROJECT_INVOICE_QUERY = gql`
  query projectInvoice($projectNumber: Int!, $invoiceNumber: Int!) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        invoices {
          id
          number
          invoice(invoiceNumber: $invoiceNumber) {
            ...invoiceFields
          }
        }
      }
    }
  }
  ${INVOICE_REQUEST_FIELDS}
`;

/**
 * Request of one invoice: with a project number the block project-invoices, without it the global
 * query invoiceByNumber. `invoiceOf` returns the `Invoice` of the response.
 */
const invoiceRequest = (invoiceNumber, projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_INVOICE_QUERY,
      variables: { projectNumber, invoiceNumber },
      invoiceOf: (data) => data.projectByNumber.blocks.invoices.invoice,
    };
  }
  return {
    query: INVOICE_BY_NUMBER_QUERY,
    variables: { invoiceNumber },
    invoiceOf: (data) => data.invoiceByNumber,
  };
};

/**
 * Request of a create: with a project number the mutation of the block project-invoices (payload
 * without project), without it the global mutation.
 */
const createInvoiceRequest = (invoice, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation createProjectInvoice($projectNumber: Int!, $payload: ProjectInvoiceInput!) {
          createProjectInvoice(projectNumber: $projectNumber, invoiceInput: $payload) {
            ...invoiceFields
          }
        }
        ${INVOICE_REQUEST_FIELDS}
      `,
      variables: { projectNumber, payload: createProjectInvoicePayload(invoice) },
      resultField: 'createProjectInvoice',
    };
  }
  return {
    mutation: gql`
      mutation createInvoice($payload: InvoiceInput!) {
        createInvoice(invoiceInput: $payload) {
          ...invoiceFields
        }
      }
      ${INVOICE_REQUEST_FIELDS}
    `,
    variables: { payload: createPayload(invoice) },
    resultField: 'createInvoice',
  };
};

/** Request of an update, chosen by the project number as `createInvoiceRequest`. */
const updateInvoiceRequest = (invoice, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation updateProjectInvoice(
          $projectNumber: Int!
          $invoiceNumber: Int!
          $payload: ProjectInvoiceInput!
        ) {
          updateProjectInvoice(
            projectNumber: $projectNumber
            invoiceNumber: $invoiceNumber
            invoiceInput: $payload
          ) {
            ...invoiceFields
          }
        }
        ${INVOICE_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber,
        invoiceNumber: invoice.number,
        payload: createProjectInvoicePayload(invoice),
      },
      resultField: 'updateProjectInvoice',
    };
  }
  return {
    mutation: gql`
      mutation updateInvoice($invoiceNumber: Int!, $payload: InvoiceInput!) {
        updateInvoice(invoiceNumber: $invoiceNumber, invoiceInput: $payload) {
          ...invoiceFields
        }
      }
      ${INVOICE_REQUEST_FIELDS}
    `,
    variables: { invoiceNumber: invoice.number, payload: createPayload(invoice) },
    resultField: 'updateInvoice',
  };
};

/**
 * Request of an attachment upload: with a project number the mutation of the block (the server
 * sets the target invoice of the project), without it the global mutation.
 */
const uploadInvoiceAttachmentRequest = ({
  file,
  parentId,
  parentType,
  invoiceNumber,
  projectNumber,
}) => {
  const attachmentInput = { referenceId: parentId, referenceType: parentType, file };
  if (projectNumber) {
    return {
      mutation: gql`
        mutation uploadProjectInvoiceAttachment(
          $projectNumber: Int!
          $invoiceNumber: Int!
          $attachmentInput: AttachmentWithTypeInput!
        ) {
          uploadProjectInvoiceAttachment(
            projectNumber: $projectNumber
            invoiceNumber: $invoiceNumber
            attachmentInput: $attachmentInput
          ) {
            ...invoiceFields
          }
        }
        ${INVOICE_REQUEST_FIELDS}
      `,
      variables: { projectNumber, invoiceNumber, attachmentInput },
      resultField: 'uploadProjectInvoiceAttachment',
    };
  }
  return {
    mutation: gql`
      mutation uploadInvoiceAttachment($attachmentInput: AttachmentWithTypeInput) {
        uploadInvoiceAttachment(attachmentInput: $attachmentInput) {
          ...invoiceFields
        }
      }
      ${INVOICE_REQUEST_FIELDS}
    `,
    variables: { attachmentInput },
    resultField: 'uploadInvoiceAttachment',
  };
};

/** Request of an attachment delete, chosen by the project number as the upload. */
const deleteInvoiceAttachmentRequest = ({
  invoiceId,
  invoiceNumber,
  attachmentId,
  projectNumber,
}) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectInvoiceAttachment(
          $projectNumber: Int!
          $invoiceNumber: Int!
          $attachmentId: ID!
        ) {
          deleteProjectInvoiceAttachment(
            projectNumber: $projectNumber
            invoiceNumber: $invoiceNumber
            attachmentId: $attachmentId
          ) {
            ...invoiceFields
          }
        }
        ${INVOICE_REQUEST_FIELDS}
      `,
      variables: { projectNumber, invoiceNumber, attachmentId },
      resultField: 'deleteProjectInvoiceAttachment',
    };
  }
  return {
    mutation: gql`
      mutation deleteInvoiceAttachment($invoiceId: ID!, $attachmentId: ID!) {
        deleteInvoiceAttachment(invoiceId: $invoiceId, attachmentId: $attachmentId) {
          ...invoiceFields
        }
      }
      ${INVOICE_REQUEST_FIELDS}
    `,
    variables: { invoiceId, attachmentId },
    resultField: 'deleteInvoiceAttachment',
  };
};

/**
 * Actions
 */
const actions = {
  /**
   * Fetch a paginated, sorted and filtered invoice list
   * @param {*} param0
   * @param {*} payload
   */
  async fetchInvoicesPaginated(
    { commit, dispatch },
    { pagination, sorting, filters, projectNumber }
  ) {
    const { query, pagesOf } = invoiceListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const invoicePages = pagesOf(response.data);
      commit('SET_INVOICES', invoicePages.invoices);
      commit('SET_INVOICE_COUNT', invoicePages.invoiceCount);
    } catch (err) {
      console.log(err);
      throw new Error(err);
    }
  },

  /** Loads one invoice; with `projectNumber` through the block project-invoices. */
  async fetchInvoiceByNumber({ commit, dispatch }, { invoiceNumber, projectNumber }) {
    try {
      const { query, variables, invoiceOf } = invoiceRequest(
        parseInt(invoiceNumber),
        projectNumber
      );
      const response = await apolloClient.query({ query, variables });
      commit('ADD_OR_UPDATE_INVOICE', invoiceOf(response.data));
    } catch (err) {
      throw new Error(err);
    }
  },

  /** Creates the invoice; with `projectNumber` through the block project-invoices. */
  async createInvoice({ commit, dispatch }, { invoice, projectNumber }) {
    const { mutation, variables, resultField } = createInvoiceRequest(invoice, projectNumber);

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Rechnung gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_INVOICE', '_new'); // remove temporary _new invoice
      commit('ADD_OR_UPDATE_INVOICE', response.data[resultField]);
      return response.data[resultField].number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Updates the invoice; with `projectNumber` through the block project-invoices. */
  async updateInvoice({ commit, dispatch }, { invoice, projectNumber }) {
    const { mutation, variables, resultField } = updateInvoiceRequest(invoice, projectNumber);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Rechnung aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_INVOICE', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  async deleteInvoice({ commit, dispatch }, invoiceNumber) {
    try {
      const response = await apolloClient.mutate({
        mutation: gql`
          mutation deleteInvoice($invoiceNumber: Int!) {
            deleteInvoice(invoiceNumber: $invoiceNumber)
          }
        `,
        variables: {
          invoiceNumber: invoiceNumber,
        },
      });
      dispatch('message', {
        message: 'Rechnung gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_INVOICE', invoiceNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new invoice object in store and return it
   * @param {*} param0
   * @param {*} invoice
   */
  async initInvoice({ commit }) {
    commit('ADD_OR_UPDATE_INVOICE', defaultInvoice());
  },

  invoiceLocalUpdate({ commit }, invoice) {
    commit('ADD_OR_UPDATE_INVOICE', invoice);
  },

  invoiceCleanup({ commit }) {
    commit('REMOVE_INVOICE', '_new');
  },

  /**
   * Upload an attachment to an invoice
   * @param {*} param0
   * @param {*} payload - contains parentId, parentType, file and, in the block, invoiceNumber and
   * projectNumber
   */
  async uploadInvoiceAttachment({ commit, dispatch }, payload) {
    const { mutation, variables, resultField } = uploadInvoiceAttachmentRequest(payload);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      commit('ADD_OR_UPDATE_INVOICE', response.data[resultField]);
      dispatch('message', {
        message: 'Anhang hochgeladen!',
        type: 'success',
        lifetime: 2,
      });
      return response.data[resultField];
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim hochladen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Delete an attachment from an invoice
   * @param {*} param0
   * @param {*} payload - contains invoiceId, attachmentId and, in the block, invoiceNumber and
   * projectNumber
   */
  async deleteInvoiceAttachment({ commit, dispatch }, payload) {
    const { mutation, variables, resultField } = deleteInvoiceAttachmentRequest(payload);
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      commit('ADD_OR_UPDATE_INVOICE', response.data[resultField]);
      dispatch('message', {
        message: 'Anhang gelöscht!',
        type: 'success',
        lifetime: 2,
      });
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_INVOICE_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_INVOICES: (state, invoices) =>
    invoices ? (state.invoices = invoices) : (state.invoices = []),
  ADD_INVOICE: (state, invoice) => state.invoices.push(invoice),
  ADD_OR_UPDATE_INVOICE: (state, updatedInvoice) => {
    const existingInvoice = state.invoices.find((invoice) => invoice._id === updatedInvoice._id);
    if (existingInvoice) {
      Object.assign(existingInvoice, updatedInvoice);
    } else {
      state.invoices.push(updatedInvoice);
    }
  },
  REMOVE_INVOICE: (state, invoiceId) => {
    const index = state.invoices.findIndex(
      (invoice) => invoice._id === invoiceId || invoice.number === invoiceId
    );
    if (index !== -1) {
      state.invoices.splice(index, 1);
    }
  },
  UPDATE_INVOICE: (state, updatedInvoice) => {
    const invoice = state.invoices.find((invoice) => invoice._id === updatedInvoice._id);
    Object.assign(invoice, updatedInvoice);
  },
  SET_INVOICE_COUNT: (state, invoiceCount) => (state.invoiceCount = invoiceCount),
};

/**
 * Getters
 */
const getters = {
  getInvoices: (state) => state.invoices,
  getInvoice: (state) => (invoiceId) => state.invoices.find((invoice) => invoice._id == invoiceId),
  getInvoicesByState: (state) => (invoiceState) =>
    state.invoices.filter((invoice) => invoice.invoiceState == invoiceState),
  getInvoicesByType: (state) => (invoiceType) =>
    state.invoices.filter((invoice) => invoice.invoiceType == invoiceType),
  getInvoiceByNumber: (state) => (invoiceNumber) =>
    state.invoices.find((invoice) => invoice.number == invoiceNumber),
  getInvoiceCount: (state) => state.invoiceCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
